import { type Easing, motion } from "framer-motion"
import { useEffect, useState } from "react"
import { AdvancedFeatureCard } from "./components/AdvancedFeatureCard"
import { advancedFeatures } from "./data/advanced-features"
import { useCarouselScroll } from "./hooks/use-carousel-scroll"
import { LandingAnimatedPaginator } from "./LandingAnimatedPaginator"

// Inject keyframes once on the client
if (typeof document !== "undefined" && !document.getElementById("flag-keyframes")) {
  const style = document.createElement("style")
  style.id = "flag-keyframes"
  style.innerHTML = `
    /* Seamless marquee: initial content visible, slide left by half-width */
    @keyframes flag-scroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }

    @keyframes flag-wave {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(4px); }
    }

    /* Fast carousel scrolling - 2x speed */
    .fast-scroll {
      scroll-behavior: smooth;
    }
    
    @media (prefers-reduced-motion: no-preference) {
      .fast-scroll {
        scroll-behavior: auto;
        transition: scroll-left 0.15s ease-out;
      }
    }
  `
  document.head.appendChild(style)
}

export function LandingBrackyAdvancedFeatures() {
  const { currentSlide, carouselRef, scrollToSlide, prevSlide, nextSlide, setCurrentSlide } = useCarouselScroll({
    itemCount: advancedFeatures.length,
    indexSwitchDelta: 30,
  })

  // Card width constants - matching Bracky message reply box widths
  const CARD_WIDTHS = {
    MOBILE: 280, // < 475px: Mobile carousel width
    SM: 0, // 475-574px: Calculated as 78% of viewport width
    MD: 448, // 575-724px: Matches max-w-md (28rem) from Bracky messages
    MDLG: 512, // 725-1023px: Matches max-w-lg (32rem) from Bracky messages
  }

  // Gap constants
  const CARD_GAPS = {
    MOBILE: 16, // Standard gap for mobile
    WIDER: 24, // 0.4rem more gap for sm/md/mdlg breakpoints
  }

  const [cardWidth, setCardWidth] = useState(CARD_WIDTHS.MOBILE)
  const [_cardGap, setCardGap] = useState(CARD_GAPS.MOBILE)
  const [containerWidth, setContainerWidth] = useState(0)
  const [chevronPressed, setChevronPressed] = useState<"left" | "right" | null>(null)

  //Style All Titles & Descriptions
  const titleStyles = "text-[1.25rem] sm:text-[1.58125rem] font-bold leading-[1.1] text-white sm:whitespace-nowrap"
  const descriptionStyles =
    "text-white/70 leading-[1.43] text-[0.95rem] sm:text-[1.0625rem] -mb-1 sm:mb-2 md:max-w-[90%] lg:max-w-[90%]"
  const cardBorderStyles = "border border-[#ffffff]/15"

  // Dynamic card width based on viewport - matches Bracky message component widths
  useEffect(() => {
    const updateCardWidth = () => {
      if (typeof window === "undefined") return

      const viewportWidth = window.innerWidth
      let newCardWidth = CARD_WIDTHS.MOBILE
      let newCardGap = CARD_GAPS.MOBILE

      if (viewportWidth >= 725 && viewportWidth < 1024) {
        // MDLG breakpoint: Use max-w-lg (32rem = 512px) to match Bracky messages
        newCardWidth = CARD_WIDTHS.MDLG
        newCardGap = CARD_GAPS.WIDER
      } else if (viewportWidth >= 575 && viewportWidth < 725) {
        // MD breakpoint: Use max-w-md (28rem = 448px) to match Bracky messages
        newCardWidth = CARD_WIDTHS.MD
        newCardGap = CARD_GAPS.WIDER
      } else if (viewportWidth >= 475 && viewportWidth < 575) {
        // SM breakpoint: Responsive width at 78% of viewport
        newCardWidth = Math.floor(viewportWidth * 0.78)
        newCardGap = CARD_GAPS.WIDER
      }

      setCardWidth(newCardWidth)
      setCardGap(newCardGap)
      // Update container width after width changes
      if (carouselRef.current) setContainerWidth(carouselRef.current.clientWidth)
    }

    updateCardWidth()
    window.addEventListener("resize", updateCardWidth)
    return () => window.removeEventListener("resize", updateCardWidth)
  }, [])

  // Keep container width in sync on resize/layout changes
  useEffect(() => {
    const updateContainerWidth = () => {
      if (carouselRef.current) setContainerWidth(carouselRef.current.clientWidth)
    }
    updateContainerWidth()
    window.addEventListener("resize", updateContainerWidth)
    return () => window.removeEventListener("resize", updateContainerWidth)
  }, [cardWidth])

  // Ensure carousel starts at first card when switching to wider breakpoints
  useEffect(() => {
    if (carouselRef.current && cardWidth !== CARD_WIDTHS.MOBILE) {
      // Reset to first card for sm/md/mdlg (any non-mobile width)
      if (cardWidth === CARD_WIDTHS.MOBILE) {
        // Center first card on mobile at init/resizes
        const cards = carouselRef.current.querySelectorAll<HTMLElement>('[data-adv-card="true"]')
        const first = cards[0]
        first?.scrollIntoView({ behavior: "auto", inline: "center", block: "nearest" })
      } else {
        carouselRef.current.scrollTo({ left: 0, behavior: "auto" })
      }
      setCurrentSlide(0)
    }
  }, [cardWidth])

  const handleScrollToSlide = (index: number) => {
    if (index > currentSlide) setChevronPressed("right")
    else if (index < currentSlide) setChevronPressed("left")
    scrollToSlide(index)
    setTimeout(() => setChevronPressed(null), 120)
  }

  // Global Arrow Left/Right keyboard navigation (one card per press)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return

      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return
      }

      const container = carouselRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const visible = rect.bottom > 0 && rect.top < window.innerHeight
      if (!visible) return

      e.preventDefault()
      if (e.key === "ArrowRight") {
        handleNextSlide()
      } else {
        handlePrevSlide()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [currentSlide])

  // Minimal animation - content always visible
  const gridVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.02,
      },
    },
  }

  const cardVariants = {
    initial: { y: 6 }, // Very small displacement
    animate: {
      y: 0,
      transition: { duration: 0.15, ease: "easeOut" as Easing },
    },
  }

  const handlePrevSlide = () => {
    setChevronPressed("left")
    prevSlide()
    setTimeout(() => setChevronPressed(null), 120)
  }

  const handleNextSlide = () => {
    setChevronPressed("right")
    nextSlide()
    setTimeout(() => setChevronPressed(null), 120)
  }

  return (
    <motion.div
      className="relative mt-[1.05rem] max-w-6xl mx-auto px-2 lg:px-[1.9rem]"
      style={{ transform: "none", willChange: "auto" }}
      variants={gridVariants}
      initial="initial"
      animate="animate"
    >
      {/* Mobile carousel */}
      <div className="lg:hidden">
        {/* Ensure vertical overflow remains visible so scaled cards aren't clipped on mobile */}
        <div className="relative">
          {/* Left Chevron - always rendered, fades in/out */}
          <button
            type="button"
            aria-label="Previous slide"
            onClick={handlePrevSlide}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-102 flex items-center justify-center text-white transition-all duration-300 ${
              currentSlide > 0
                ? `opacity-70 pointer-events-auto ${chevronPressed === "left" ? "opacity-40" : ""}`
                : "opacity-0 pointer-events-none"
            }`}
            style={{ height: "70%", width: "1.75rem", touchAction: "pan-x" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <title>Previous slide</title>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Right Chevron - always rendered, fades in/out */}
          <button
            type="button"
            aria-label="Next slide"
            onClick={handleNextSlide}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-102 flex items-center justify-center text-white transition-all duration-300 ${
              currentSlide < advancedFeatures.length - 1
                ? `opacity-70 pointer-events-auto ${chevronPressed === "right" ? "opacity-40" : ""}`
                : "opacity-0 pointer-events-none"
            }`}
            style={{ height: "70%", width: "1.75rem", touchAction: "pan-x" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <title>Next slide</title>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>

          <div
            ref={carouselRef}
            className="flex gap-4 sm:gap-6 lg:gap-4 overflow-x-auto scrollbar-hide pb-4 pt-2 snap-x snap-mandatory -mx-2 fast-scroll"
            style={{
              overflowY: "visible",
              // Different padding for mobile vs wider breakpoints
              ...(cardWidth === CARD_WIDTHS.MOBILE
                ? {
                    // Mobile: Original offsets with side padding
                    scrollPaddingLeft: `calc((100vw - ${cardWidth}px) / 2)`,
                    scrollPaddingRight: `calc((100vw - ${cardWidth}px) / 2 + 0.97rem)`,
                    paddingLeft: "1.5rem",
                    paddingRight: "1.47rem",
                  }
                : {
                    // SM/MD/MDLG: Use actual container width to perfectly center first/last
                    scrollPaddingLeft: `${Math.max(0, (containerWidth - cardWidth) / 2)}px`,
                    scrollPaddingRight: `${Math.max(0, (containerWidth - cardWidth) / 2)}px`,
                    paddingLeft: `${Math.max(0, (containerWidth - cardWidth) / 2)}px`,
                    paddingRight: `${Math.max(0, (containerWidth - cardWidth) / 2)}px`,
                  }),
            }}
          >
            {advancedFeatures.map((feature, index) => (
              <AdvancedFeatureCard
                key={feature.title}
                feature={feature}
                index={index}
                isDesktop={false}
                titleStyles={titleStyles}
                descriptionStyles={descriptionStyles}
                cardBorderStyles={cardBorderStyles}
                cardVariants={cardVariants}
                onClick={() => handleScrollToSlide(index)}
                width={`${cardWidth}px`}
              />
            ))}
          </div>
        </div>

        {/* Pagination dots replaced by reusable component */}
        <LandingAnimatedPaginator
          total={advancedFeatures.length}
          active={currentSlide}
          onDotClick={handleScrollToSlide}
          className="mt-[.62rem]"
        />
      </div>

      {/* Desktop grid: 2×2 elongated layout for first 4 features, with a single orphan card */}
      <div className="hidden lg:flex flex-col gap-6" style={{ overflowY: "visible" }}>
        {/* 2×2 grid – first four features */}
        <div className="grid grid-cols-2 grid-rows-2 gap-y-6 gap-x-6">
          {advancedFeatures.slice(0, 4).map((feature, index) => (
            <AdvancedFeatureCard
              key={feature.title}
              feature={feature}
              index={index}
              isDesktop={true}
              titleStyles={titleStyles}
              descriptionStyles={descriptionStyles}
              cardBorderStyles={cardBorderStyles}
              cardVariants={cardVariants}
            />
          ))}
        </div>

        {/* Orphan card – 5th feature */}
        <div className="flex justify-center">
          {advancedFeatures.slice(4).map((feature, index) => (
            <AdvancedFeatureCard
              key={feature.title}
              feature={feature}
              index={index + 4}
              isDesktop={true}
              titleStyles={titleStyles}
              descriptionStyles={descriptionStyles}
              cardBorderStyles={cardBorderStyles}
              cardVariants={cardVariants}
              width="calc(50% - 0.75rem)"
            />
          ))}
        </div>
      </div>
    </motion.div>
  )
}
